import React from 'react';
import PropTypes from 'prop-types';

import AppBar from './AppBar';
import {useUser} from '../utils/UserSession';
import {
  isDocenteSectionSelected,
  shouldShowDocenteSectionChoice,
} from '../utils/membershipUi';
import {
  PersonOutline,
  CollectionsBookmark,
  CreditCard,
  Assignment,
  Loyalty,
  Extension,
} from '@material-ui/icons';

// Rebranding 2026 · AppHeader → gc-topbar + gc-infoband (2 fasce)

const navIcons = {
  account: PersonOutline,
  practices: CollectionsBookmark,
  payments: CreditCard,
  documents: Assignment,
  vouchers: Loyalty,
  upselling: Extension,
};

const NavLink = ({to, label, icon, selected}) => {
  const Icon = navIcons[icon] || PersonOutline;
  return (
    <a href={to} className={`gc-sc-iconlink${selected ? ' gc-sc-iconlink--selected' : ''}`}>
      <span className="gc-sc-iconlink__icon">
        <Icon />
      </span>
      <span className="gc-sc-iconlink__text">{label}</span>
    </a>
  );
};

const AppHeader = ({route, section, dynamicSections = []}) => {
  const {user} = useUser();

  const isDocenteSelected = isDocenteSectionSelected();
  const isDocenteChoice = shouldShowDocenteSectionChoice(user);

  // Se l'utente è in modalità "scegli profilo" → infoband choice, senza sidebar
  if (isDocenteChoice) {
    return (
      <>
        <AppBar />
        <div className="gc-infoband" aria-labelledby="gc-page-title">
          <div className="gc-infoband__gwm" />
          <div className="gc-infoband__inner">
            <div className="gc-infoband__text gc-infoband__text--full">
              <p className="gc-infoband__eyebrow">
                Bentornata {user.firstName} · doppio ruolo
              </p>
              <h1 id="gc-page-title" className="gc-infoband__title">Da dove vuoi cominciare?</h1>
              <p className="gc-infoband__desc">
                Il tuo account ha sia un profilo cliente che un profilo accompagnatore.
                Scegli con quale dei due vuoi accedere: potrai cambiare in qualsiasi momento dal menu in alto.
              </p>
              <div className="gc-infoband__sep" />
            </div>
          </div>
        </div>
      </>
    );
  }

  // Utente normale (member o docente con ruolo selezionato)
  const {title, description, eyebrow} = getScreenText(route, user, section, isDocenteSelected);

  return (
    <>
      <AppBar />
      <div className="gc-infoband" aria-labelledby="gc-page-title">
        <div className="gc-infoband__gwm" />
        <div className="gc-infoband__inner">
          <div className="gc-infoband__text">
            {eyebrow && (
              <p className="gc-infoband__eyebrow">
                {eyebrow}
              </p>
            )}
            <h1 id="gc-page-title" className="gc-infoband__title">{title}</h1>
            <p className="gc-infoband__desc" dangerouslySetInnerHTML={{__html: description}} />
            <div className="gc-infoband__sep" />
          </div>

          <div className="gc-infoband__aside">
            {!isDocenteSelected && <PointsBoxIvory user={user} />}
            <nav className="gc-sc-appheader__links" aria-label="Navigazione area riservata">
              <NavLink to="/account" label="Informazioni account" icon="account" selected={route === '/account'} />
              {!isDocenteSelected && <>
                <NavLink to="/practices" label="Prenotazioni" icon="practices" selected={route === '/practices'} />
                <NavLink to="/payments" label="Pagamenti/Fatture" icon="payments" selected={route === '/payments'} />
                <NavLink to="/documents" label="Contratti/Documenti" icon="documents" selected={route === '/documents'} />
                <NavLink to="/vouchers" label="Voucher" icon="vouchers" selected={route === '/vouchers'} />
                <NavLink to="/upselling" label="Personalizza la tua esperienza" icon="upselling" selected={route === '/upselling'} />
                {dynamicSections.map((s) => (
                  <NavLink key={s.id} to={`/${s.toPath}`} label={s.label} icon={s.icon} selected={route === `/${s.toPath}`} />
                ))}
              </>}
            </nav>
          </div>
        </div>
      </div>
    </>
  );
};

const getScreenText = (route, user, section, isDocente) => {
  if (section !== undefined && `/${section.toPath}` === route) {
    return {title: section.title, description: section.subtitle, eyebrow: 'Area riservata'};
  }

  if (route === '/account') {
    return {
      eyebrow: 'Area riservata · Il tuo profilo',
      title: 'Informazioni Account',
      description: 'Qui puoi vedere ed editare tutte le informazioni relative al tuo account Giocamondo, gestire le credenziali di accesso e i partecipanti collegati.',
    };
  }

  if (route === '/practices') {
    return {
      eyebrow: 'Area riservata · Le tue pratiche',
      title: 'Prenotazioni',
      description: 'In questa sezione puoi visualizzare tutte le tue prenotazioni. Cliccando sulla riga di ciascuna pratica potrai consultare i dettagli e, quando disponibile, effettuare in autonomia modifiche.',
    };
  }

  if (route === '/payments') {
    return {
      eyebrow: 'Area riservata · Contabilità',
      title: 'Pagamenti e Fatture',
      description: 'In questa sezione puoi visualizzare la situazione contabile delle tue prenotazioni e scaricare la documentazione fiscale.',
    };
  }

  if (route === '/documents') {
    return {
      eyebrow: 'Area riservata · Documentazione',
      title: 'Contratti e Documenti',
      description: 'In questa sezione potrai visionare i tuoi contratti e procedere al caricamento dei documenti qualora fosse necessario.',
    };
  }

  if (route === '/vouchers') {
    return {
      eyebrow: 'Area riservata · I tuoi vantaggi',
      title: 'Voucher',
      description: 'In questa sezione puoi visualizzare i tuoi voucher accumulati e quelli ancora disponibili.',
    };
  }

  if (route === '/upselling') {
    return {
      eyebrow: 'Area riservata · Servizi extra',
      title: 'Personalizza la tua esperienza',
      description: 'Scegli i servizi più adatti per vivere al meglio il tuo soggiorno. Puoi modificare la prenotazione finché è in stato opzionato.',
    };
  }

  return {
    eyebrow: 'Benvenuto nel Club',
    title: `Ciao ${user.firstName}!`,
    description: isDocente
      ? "Benvenuto nella tua area riservata da accompagnatore. Qui potrai monitorare tutte le informazioni aggiornate relative alle prenotazioni degli studenti."
      : "<strong>Benvenuto nella tua area riservata Giocamondo Club</strong>. Da qui hai il pieno controllo su tutte le tue attività e prenotazioni e puoi vedere in tempo reale i progressi nella Membership.",
  };
};

AppHeader.propTypes = {
  route: PropTypes.string.isRequired,
};

// --- PointsBox Ivory · widget punti antracite + chip tier + CTA voucher
// NB: l'indicatore "A {tier} · N pt" è stato rimosso in attesa che le soglie
// dei livelli vengano esposte dal backend (vedi Synapsy core). Per riattivarlo:
// scommentare il blocco TIER_LADDER/TIER_THRESHOLDS qui sotto e la <p .__next> nel JSX.

const PointsBoxIvory = ({user}) => {
  const points = Number(user.points) || 0;
  const tier = (user.clusterCode || 'BLUE').toUpperCase();
  const tierClass = `gc-tier--${tier.toLowerCase()}`;

  return (
    <div className="gc-sc-pointsbox" aria-label={`Punti accumulati: ${points.toLocaleString('it-IT')}`}>
      <p className="gc-sc-pointsbox__label">Punti accumulati</p>
      <p className="gc-sc-pointsbox__number" aria-hidden="true">{points.toLocaleString('it-IT')}</p>
      <div className={`gc-sc-pointsbox__chip ${tierClass}`}>{tier}</div>
      <a href="/vouchers" className="gc-sc-pointsbox__cta">Converti in Voucher</a>
    </div>
  );
};

export default AppHeader;
